import * as Sentry from "@sentry/react";

// Initialize Sentry
export const initSentry = async () => {
  try {
    console.log('🔧 Initializing Sentry...');
    
    // Fetch DSN from edge function
    const response = await fetch('https://nzkgsdntqmbknznbgnii.supabase.co/functions/v1/sentry-config');
    
    if (!response.ok) {
      console.warn('❌ Failed to fetch Sentry config:', response.status, response.statusText);
      return;
    }
    
    const data = await response.json();
    console.log('📡 Sentry config response:', data);
    
    if (!data.dsn) {
      console.warn('⚠️ Sentry DSN not available, skipping initialization');
      return;
    }

    console.log('✅ Sentry DSN found, initializing...');
    Sentry.init({
      dsn: data.dsn,
      environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, // Capture 100% of the transactions, reduce in production
    
    // Session Replay
    replaysSessionSampleRate: 0.1, // 10% of sessions will be recorded
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with an error will be recorded
    
    beforeSend(event, hint) {
      // Filter out some common non-critical errors
      if (event.exception) {
        const error = hint.originalException;
        
        // Filter out network errors that are not actionable
        if (error && typeof error === 'object' && 'message' in error) {
          const message = (error as Error).message;
          if (
            message.includes('Network Error') ||
            message.includes('Failed to fetch') ||
            message.includes('ERR_NETWORK') ||
            message.includes('ERR_INTERNET_DISCONNECTED')
          ) {
            return null;
          }
        }
      }
      
      return event;
    },
    
    // Add user context
    initialScope: {
      tags: {
        component: "logistics-platform"
      }
    }
    });
    
    console.log('🎉 Sentry initialized successfully!');
    
    // Test capture to confirm it's working
    Sentry.addBreadcrumb({
      message: 'Sentry initialization completed',
      category: 'system',
      level: 'info'
    });
    
  } catch (error) {
    console.error('❌ Failed to initialize Sentry:', error);
  }
};

// Helper function to capture user context
export const setSentryUser = (user: {
  id: string;
  email?: string;
  username?: string;
  organization?: string;
}) => {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username,
    organization: user.organization,
  });
};

// Helper function to add breadcrumbs for user actions
export const addSentryBreadcrumb = (message: string, category: string, data?: any) => {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  });
};

// Helper function to capture custom errors
export const captureError = (error: Error, context?: Record<string, any>) => {
  Sentry.withScope((scope) => {
    if (context) {
      Object.keys(context).forEach(key => {
        scope.setContext(key, context[key]);
      });
    }
    Sentry.captureException(error);
  });
};

// Helper function to capture messages
export const captureMessage = (message: string, level: Sentry.SeverityLevel = 'info') => {
  Sentry.captureMessage(message, level);
};

export { Sentry };